import type { JSX } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import type { CookSessionApiItem } from "@recipevault/shared";
import { sync } from "../../sync/SyncService.js";
import { setFocusedSessionId } from "./focusedSession.js";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "../../ui/sheet.js";
import { Button } from "../../ui/button.js";

export interface WaitingSessionSheetProps {
  session: CookSessionApiItem | null;
  recipeTitle?: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Formats a wakeUpAt ISO string into a human-readable local time. */
function formatWakeTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export function WaitingSessionSheet({
  session,
  recipeTitle,
  open,
  onOpenChange,
}: WaitingSessionSheetProps): JSX.Element {
  const navigate = useNavigate();

  async function handleContinue(): Promise<void> {
    if (!session) return;
    try {
      await sync.resumeCookSessionOptimistic(session.id);
      setFocusedSessionId(session.id);
      onOpenChange(false);
      void navigate("/cook/" + session.id);
    } catch (err) {
      toast.error("Couldn't resume session. Please try again.");
      throw err;
    }
  }

  async function handleAbandon(): Promise<void> {
    if (!session) return;
    try {
      await sync.abandonCookSessionOptimistic(session.id);
      onOpenChange(false);
      toast.success("Cook session cancelled.");
    } catch (err) {
      toast.error("Couldn't cancel session. Please try again.");
      throw err;
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>Waiting — {recipeTitle ?? "Recipe"}</SheetTitle>
          {session?.wakeUpReason && (
            <SheetDescription>{session.wakeUpReason}</SheetDescription>
          )}
          {session?.wakeUpAt && (
            <p className="text-sm text-zinc-400 pt-1">
              Set for {formatWakeTime(session.wakeUpAt)}
            </p>
          )}
        </SheetHeader>
        <SheetFooter>
          <Button onClick={() => { void handleContinue(); }}>
            Continue cooking now
          </Button>
          <Button
            variant="destructive"
            onClick={() => { void handleAbandon(); }}
          >
            Cancel — abandon
          </Button>
          <Button variant="outline" onClick={() => { onOpenChange(false); }}>
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
